/** Width of the Documents floater panel (`40rem + 4px`). */
export const DOCUMENTS_PANEL_WIDTH = 644

export const AI_PANEL_GAP = 8

export type AssistantPositionMode = 'docked-right' | 'floating'

export interface AssistantFloatingPosition {
  top: number
  right: number
}

export interface DocumentsPanelLayoutDetail {
  open: boolean
  side: 'left' | 'right'
  width: number
}

export const DOCUMENTS_PANEL_EVENT = 'companion-panels:documents'

export const POSITION_MODE_STORAGE_KEY = 'aiAssistantPositionMode'
export const FLOATING_POSITION_STORAGE_KEY = 'aiAssistantFloatingPosition'

export const notifyDocumentsPanel = (detail: DocumentsPanelLayoutDetail) => {
  window.dispatchEvent(new CustomEvent(DOCUMENTS_PANEL_EVENT, { detail }))
}

export const readStoredPositionMode = (): AssistantPositionMode => {
  try {
    const stored = localStorage.getItem(POSITION_MODE_STORAGE_KEY)
    return stored === 'floating' ? 'floating' : 'docked-right'
  } catch {
    return 'docked-right'
  }
}

export const writeStoredPositionMode = (mode: AssistantPositionMode) => {
  try {
    localStorage.setItem(POSITION_MODE_STORAGE_KEY, mode)
  } catch {
    // Storage can throw in private mode.
  }
}

export const readStoredFloatingPosition = (): AssistantFloatingPosition => ({
  top: Math.max(48, window.innerHeight * 0.05),
  right: DOCUMENTS_PANEL_WIDTH + AI_PANEL_GAP,
})

export const loadStoredFloatingPosition = (): AssistantFloatingPosition => {
  try {
    const raw = localStorage.getItem(FLOATING_POSITION_STORAGE_KEY)
    if (!raw) return readStoredFloatingPosition()
    const parsed = JSON.parse(raw)
    if (typeof parsed?.top === 'number' && typeof parsed?.right === 'number') {
      return { top: parsed.top, right: parsed.right }
    }
  } catch {
    // Ignore corrupt storage.
  }
  return readStoredFloatingPosition()
}

export const writeStoredFloatingPosition = (position: AssistantFloatingPosition) => {
  try {
    localStorage.setItem(FLOATING_POSITION_STORAGE_KEY, JSON.stringify(position))
  } catch {
    // Storage can throw in private mode.
  }
}

/** Place the assistant immediately to the left of an open right-docked Documents panel. */
export const floatingPositionBesideDocuments = (): AssistantFloatingPosition => ({
  top: Math.max(48, window.innerHeight * 0.05),
  right: DOCUMENTS_PANEL_WIDTH + AI_PANEL_GAP,
})

export const clampFloatingPosition = (
  position: AssistantFloatingPosition,
  panelWidth: number,
  panelHeight: number,
): AssistantFloatingPosition => {
  const margin = 8
  const maxTop = Math.max(margin, window.innerHeight - panelHeight - margin)
  const maxRight = Math.max(margin, window.innerWidth - panelWidth - margin)
  return {
    top: Math.min(Math.max(position.top, margin), maxTop),
    right: Math.min(Math.max(position.right, margin), maxRight),
  }
}
